import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { SectionCard, Loading, EmptyState, Money } from "@/components/common/ui";
import { KpiTile } from "@/components/KpiTile";
import { GovernedEditableField } from "@/components/GovernedEditableField";
import { KpiValueEditor } from "@/components/dashboard/KpiValueEditor";
import { ProjectVerticalsDialog } from "@/components/dashboard/ProjectVerticalsDialog";
import { EditableText } from "@/components/cms/EditableText";
import { FunnelStageBar } from "@/components/FunnelStageBar";
import { StageBadge } from "@/pages/AnalyticsHub";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import { TargetBadge } from "@/components/TargetBadge";
import { C, axisProps, seriesColor, piePctLabel } from "@/lib/chart";
import { formatCount, formatPercent, axisINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Cell, PieChart, Pie,
} from "recharts";
import {
  ArrowLeft, Wallet, PiggyBank, IndianRupee, Home, Ruler, Target, Eye, Activity, Layers, SlidersHorizontal,
} from "lucide-react";

const DASH = "\u2014";
const VIS_COLORS = { Visibility: "hsl(var(--chart-cat-4))", Performance: "hsl(var(--chart-cat-3))" };
const MONTHS = ["Apr-26", "May-26", "Jun-26", "Jul-26", "Aug-26", "Sep-26", "Oct-26", "Nov-26", "Dec-26", "Jan-27", "Feb-27", "Mar-27"];
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

// PART 8 — admin-toggleable Project Detail sections (persisted in dashboard_config.cards).
const PD_SECTIONS = [
  { key: "pd-kpis-financial", label: "Financial KPIs" },
  { key: "pd-kpis-sales", label: "Sales & Split KPIs" },
  { key: "pd-trend", label: "Monthly Trend" },
  { key: "pd-funnel", label: "Funnel (FY total)" },
  { key: "pd-vertical-chart", label: "Budget vs Spend by Vertical" },
  { key: "pd-visperf", label: "Visibility vs Performance" },
  { key: "pd-vertical-seg", label: "Spend by Vertical \u2014 Segregation" },
  { key: "pd-platform", label: "Spend by Platform" },
  { key: "pd-monthly-table", label: "Monthly Funnel & Revenue" },
  { key: "pd-line-items", label: "Spend Line Items" },
];

export default function ProjectDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const { money } = useCurrency();
  const { isCmsAdmin } = useAuth();
  const canEditRev = isCmsAdmin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [gran, setGran] = useState("Yearly");
  const [quarter, setQuarter] = useState("Q1");
  const [month, setMonth] = useState("Apr-26");
  const period = gran === "Yearly" ? "FY" : gran === "Quarterly" ? quarter : month;

  const reload = () => api.projectDetail(name, period).then((d) => setData(d)).catch(() => {});
  const saveTargetedRevenue = async (value, reason) => {
    await api.editDashboardValue({ project: name, month: "FY", field: "targeted_revenue", value, reason });
    await reload();
  };

  useEffect(() => {
    let on = true; setLoading(true);
    api.projectDetail(name, period).then((d) => { if (on) { setData(d); setLoading(false); } }).catch(() => on && setLoading(false));
    return () => { on = false; };
  }, [name, period]);

  // PART 8 — global dashboard config drives section visibility (shared with the dashboard).
  useEffect(() => { api.dashboardConfig().then(setConfig).catch(() => setConfig(null)); }, []);

  // PART 8 — realtime propagation: refetch when another admin edits KPIs/data/layout.
  useEffect(() => {
    const onRt = (e) => {
      const type = e?.detail?.type;
      if (type === "dashboard:config") api.dashboardConfig().then(setConfig).catch(() => {});
      else if (type === "kpi:update" || type === "data:update") reload();
    };
    window.addEventListener("realtime:update", onRt);
    return () => window.removeEventListener("realtime:update", onRt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, period]);

  if (loading && !data) return <Loading />;
  if (!data) return <EmptyState title="Project not found" message="No data for this project." />;
  const s = data.summary;
  const isPrimary = s.tier === "primary";
  const moneyFmt = (v) => money(v);
  const hasActivity = (data.monthly || []).length > 0;
  const utilPct = Math.min(100, Math.round((s.budget_util || 0) * 100));
  const plannedUtilPct = s.planned_budget ? Math.round((s.spend / s.planned_budget) * 100) : 0;
  const periodLabel = gran === "Yearly" ? "Full Year FY26-27" : gran === "Quarterly" ? quarter : month;
  const verticalRows = (data.by_vertical || []);
  const activeVerticals = verticalRows.filter((v) => v.spend > 0 || v.budget > 0).length;
  // PART 9 — Budget Variance Flags: a vertical is "over plan" when spend exceeds its planned budget.
  const isOverPlan = (v) => (v.budget || 0) > 0 && (v.spend || 0) > (v.budget || 0);
  const overPlanCount = verticalRows.filter(isOverPlan).length;

  // ---- PART 8: inline KPI overrides (admin-only) — same mechanism as the dashboard ----
  const ovr = s.overridden || {};
  const saveKpi = (field) => async (value) => {
    await api.overrideDashboardKpi({ period, projects: name, field, value });
    await reload();
  };
  const resetKpi = (field) => async () => {
    await api.overrideDashboardKpi({ period, projects: name, field, value: null });
    await reload();
  };

  // ---- PART 8: section visibility (persisted globally so it propagates to everyone) ----
  const cards = config?.cards || {};
  const pdOn = (key) => cards[key] !== false; // default visible
  const togglePdCard = async (key, visible) => {
    const body = { ...(config || {}), cards: { ...cards, [key]: visible } };
    setConfig(body);
    try { await api.saveDashboardConfig(body); toast.success(visible ? "Section shown" : "Section hidden"); }
    catch { toast.error("Could not update section visibility"); }
  };

  // Spend by Platform — Top N + reconciling "Other" bucket so the chart total == Total Spend.
  const TOP_PLATFORMS = 9;
  const platformAll = (data.by_platform || []);
  const platformTotal = platformAll.reduce((a, p) => a + (p.spend || 0), 0);
  const platformRows = (() => {
    if (platformAll.length <= TOP_PLATFORMS + 1) return platformAll;
    const top = platformAll.slice(0, TOP_PLATFORMS);
    const rest = platformAll.slice(TOP_PLATFORMS);
    const otherSpend = rest.reduce((a, p) => a + (p.spend || 0), 0);
    return [...top, { platform: `Other (${rest.length})`, spend: Math.round(otherSpend * 100) / 100, _other: true }];
  })();

  return (
    <div className="space-y-5" data-testid="project-detail">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={() => navigate("/projects")} data-testid="back-to-projects" aria-label="Back to projects" className="h-9 gap-1.5 px-3"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="project-detail-name">{data.project}</h1>
            <span className={"rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase " + (isPrimary ? "bg-[hsl(var(--primary)/0.12)] text-primary" : "bg-[hsl(var(--surface-3))] text-muted-foreground")} data-testid="project-tier-badge">{isPrimary ? "Primary" : "Secondary"}</span>
            <StageBadge stage={data.stage || "Other"} testid="project-detail-stage-badge" />
          </div>
          <p className="text-sm text-muted-foreground">{s.portfolio || "Unclassified"} · {s.invoices} line items · {periodLabel}{isPrimary ? " · target-based plan" : " · spend-only"}</p>
        </div>

        {/* Period filter (Phase J) + PART 8 admin section manager */}
        <div className="ml-auto flex flex-wrap items-center gap-2" data-testid="project-detail-period-filter">
          {isCmsAdmin && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 gap-1.5 px-3" data-testid="pd-manage-sections-btn">
                  <SlidersHorizontal className="h-4 w-4" /> Sections
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72" data-testid="pd-manage-sections-panel">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Show / hide sections</p>
                <div className="space-y-1">
                  {PD_SECTIONS.map((sec) => (
                    <div key={sec.key} className="flex items-center justify-between rounded-md border bg-card px-3 py-1.5" data-testid={`pd-section-row-${sec.key}`}>
                      <span className="text-sm">{sec.label}</span>
                      <Switch checked={pdOn(sec.key)} onCheckedChange={(v) => togglePdCard(sec.key, v)} data-testid={`pd-section-toggle-${sec.key}`} />
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
          <ToggleGroup type="single" value={gran} onValueChange={(v) => v && setGran(v)} className="rounded-lg border bg-card p-0.5" data-testid="project-detail-granularity-toggle">
            <ToggleGroupItem value="Monthly" className="h-7 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid="project-detail-gran-monthly">Month</ToggleGroupItem>
            <ToggleGroupItem value="Quarterly" className="h-7 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid="project-detail-gran-quarterly">Quarter</ToggleGroupItem>
            <ToggleGroupItem value="Yearly" className="h-7 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid="project-detail-gran-yearly">Year</ToggleGroupItem>
          </ToggleGroup>
          {gran === "Quarterly" && (
            <Select value={quarter} onValueChange={setQuarter}>
              <SelectTrigger className="h-9 w-[110px]" data-testid="project-detail-quarter-select"><SelectValue /></SelectTrigger>
              <SelectContent>{QUARTERS.map((q) => <SelectItem key={q} value={q} data-testid={`project-detail-quarter-${q}`}>{q}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {gran === "Monthly" && (
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="h-9 w-[128px]" data-testid="project-detail-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={m} data-testid={`project-detail-month-${m}`}>{m}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Financial KPIs — parity with the executive dashboard (admin-editable values) */}
      {pdOn("pd-kpis-financial") && (
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 xl:grid-cols-5" data-testid="pd-kpis-financial">
        <KpiTile testId="pd-budget-allocated" title="Budget Allocated" accent="hsl(var(--primary))" icon={PiggyBank}
          value={<KpiValueEditor testId="pd-kpi-edit-budget-allocated" value={s.budget_allocated || 0} overridden={!!ovr.budget_allocated} canEdit={isCmsAdmin} onSave={saveKpi("budget_allocated")} onReset={resetKpi("budget_allocated")}
            display={s.budget_allocated ? <Money value={s.budget_allocated} /> : DASH} />}
          sub={s.budget_allocated ? `${utilPct}% utilised` : "No allocation"}>
          {s.budget_allocated ? <div className="h-2 w-full overflow-hidden rounded-full bg-[hsl(var(--surface-3))]"><div className="h-full rounded-full" style={{ width: `${utilPct}%`, background: (s.budget_util || 0) > 1 ? "hsl(var(--danger))" : "hsl(var(--primary))" }} /></div> : null}
        </KpiTile>
        <KpiTile testId="pd-planned-budget" title="Planned Budget" accent="hsl(var(--info))" icon={Layers}
          value={<KpiValueEditor testId="pd-kpi-edit-planned-budget" value={s.planned_budget || 0} overridden={!!ovr.planned_budget} canEdit={isCmsAdmin} onSave={saveKpi("planned_budget")} onReset={resetKpi("planned_budget")}
            display={s.planned_budget ? <Money value={s.planned_budget} /> : DASH} />}
          sub={s.planned_budget ? `${plannedUtilPct}% of plan spent` : "No plan set"}>
          {s.planned_budget ? <div className="h-2 w-full overflow-hidden rounded-full bg-[hsl(var(--surface-3))]"><div className="h-full rounded-full" style={{ width: `${Math.min(100, plannedUtilPct)}%`, background: plannedUtilPct > 100 ? "hsl(var(--danger))" : "hsl(var(--info))" }} /></div> : null}
        </KpiTile>
        <KpiTile testId="pd-spend" title="Total Spend" accent="hsl(var(--chart-cat-2))" icon={Wallet}
          value={<KpiValueEditor testId="pd-kpi-edit-total-spends" value={s.spend || 0} overridden={!!ovr.total_spends} canEdit={isCmsAdmin} onSave={saveKpi("total_spends")} onReset={resetKpi("total_spends")}
            display={<Money value={s.spend} />} />}
          sub={`${s.invoices} line items`} />
        <KpiTile testId="pd-revenue" title="Revenue" accent="hsl(var(--chart-cat-1))" icon={IndianRupee} value={<Money value={s.revenue} />} sub={s.targeted_revenue ? `${formatPercent(s.revenue_vs_target, 1)} of target` : "Realised income"} />
        <KpiTile testId="pd-targeted-revenue" title="Targeted Revenue" accent="hsl(var(--chart-cat-6))" icon={Target}
          value={canEditRev
            ? <GovernedEditableField value={s.targeted_revenue || 0} display={(v) => (v ? <Money value={v} /> : DASH)} canEdit onSave={saveTargetedRevenue} label={`Targeted Revenue \u00b7 ${data.project} \u00b7 FY`} testPrefix="pd-targeted-revenue" align="left" showLock={false} valueClassName="tabular-nums" />
            : (s.targeted_revenue ? <Money value={s.targeted_revenue} /> : DASH)}
          sub={isPrimary ? "Annual income goal" : "Not targeted"} />
      </div>
      )}

      {/* Sales / split KPIs */}
      {pdOn("pd-kpis-sales") && (
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4" data-testid="pd-kpis-sales">
        <KpiTile testId="pd-units" title="Units Sold" accent="hsl(var(--funnel-bookings))" icon={Home} value={`${formatCount(s.units_sold)} / ${formatCount(s.units_target)}`} sub={isPrimary ? "vs unit target" : "logged units"} />
        <KpiTile testId="pd-psf" title="Avg ₹ / Sq.Ft" accent="hsl(var(--chart-cat-3))" icon={Ruler} value={s.avg_psf ? <Money value={s.avg_psf} /> : DASH} sub={`${formatCount(s.sold_sqft)} sq.ft sold`} />
        <KpiTile testId="pd-visibility" title="Visibility Spend" accent="hsl(var(--chart-cat-4))" icon={Eye} value={<Money value={s.visibility_spend} />} sub="awareness" />
        <KpiTile testId="pd-performance" title="Performance Spend" accent="hsl(var(--chart-cat-3))" icon={Activity} value={<Money value={s.performance_spend} />} sub="lead-gen" />
      </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {pdOn("pd-trend") && (
        <SectionCard title={<EditableText cmsKey="pd.section.trend.title" fallback="Monthly Trend — Spend, Revenue & Budget" />} description="Bars: spend & revenue. Dashed line: monthly budget." className="xl:col-span-2" testId="card-pd-trend">
          <div className="px-2 py-4" style={{ height: 300 }}>
            {!hasActivity ? <EmptyState title="No activity" message="No spend or revenue recorded." /> : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                  <XAxis dataKey="month" {...axisProps} />
                  <YAxis tickFormatter={axisINR} {...axisProps} width={48} />
                  <Tooltip content={<ChartTooltip formatter={(v) => moneyFmt(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="spend" name="Spend" fill={C.spend} radius={[4, 4, 0, 0]} barSize={16} isAnimationActive={false} />
                  <Bar dataKey="revenue" name="Revenue" fill="hsl(var(--chart-cat-1))" radius={[4, 4, 0, 0]} barSize={16} isAnimationActive={false} />
                  <Line type="monotone" dataKey="budget" name="Budget" stroke="hsl(var(--chart-cat-7))" strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>
        )}

        {pdOn("pd-funnel") && (
        <SectionCard title={<EditableText cmsKey="pd.section.funnel.title" fallback="Funnel (FY total)" />} testId="card-pd-funnel">
          <div className="p-4"><FunnelStageBar leads={{ target: s.leads_target, actual: s.leads_actual }} visits={{ target: s.sv_target, actual: s.sv_actual }} bookings={{ target: s.booking_target, actual: s.booking_actual }} /></div>
        </SectionCard>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {pdOn("pd-vertical-chart") && (
        <SectionCard title={<EditableText cmsKey="pd.section.vertical_chart.title" fallback="Budget vs Spend by Vertical" />} className="lg:col-span-2" testId="card-pd-vertical">
          <div className="px-2 py-4" style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_vertical} margin={{ top: 8, right: 12, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                <XAxis dataKey="vertical" angle={-20} textAnchor="end" height={56} interval={0} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 9 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
                <YAxis tickFormatter={axisINR} {...axisProps} width={46} />
                <Tooltip content={<ChartTooltip formatter={(v) => moneyFmt(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="budget" name="Budget" fill="hsl(var(--chart-cat-7))" radius={[4, 4, 0, 0]} barSize={14} isAnimationActive={false} />
                <Bar dataKey="spend" name="Spend" fill="hsl(var(--chart-cat-1))" radius={[4, 4, 0, 0]} barSize={14} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
        )}

        {pdOn("pd-visperf") && (
        <SectionCard title={<EditableText cmsKey="pd.section.visperf.title" fallback="Visibility vs Performance" />} testId="card-pd-visperf">
          <div className="px-2 py-4" style={{ height: 300 }}>
            {(s.visibility_spend + s.performance_spend) === 0 ? <EmptyState title="No split" message="No visibility/performance spend." /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.by_vis_perf} dataKey="spend" nameKey="name" innerRadius={56} outerRadius={92} paddingAngle={2} stroke="none"
                    labelLine={false} label={piePctLabel} isAnimationActive={false}>
                    {data.by_vis_perf.map((e) => <Cell key={e.name} fill={VIS_COLORS[e.name] || "hsl(var(--chart-cat-7))"} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip formatter={(v) => {
                    const tot = (s.visibility_spend || 0) + (s.performance_spend || 0);
                    return `${moneyFmt(v)} · ${tot ? Math.round((v / tot) * 100) : 0}%`;
                  }} />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} formatter={(nm) => {
                    const tot = (s.visibility_spend || 0) + (s.performance_spend || 0);
                    const val = nm === "Visibility" ? s.visibility_spend : s.performance_spend;
                    return `${nm} · ${tot ? Math.round(((val || 0) / tot) * 100) : 0}%`;
                  }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>
        )}
      </div>

      {/* 14-vertical segregation (Phase J) */}
      {pdOn("pd-vertical-seg") && (
      <SectionCard title={<EditableText cmsKey="pd.section.vertical_segregation.title" fallback="Spend by Vertical — Segregation" />}
        description={`Budget vs spend across all ${verticalRows.length} marketing verticals for ${periodLabel} · ${activeVerticals} active${overPlanCount ? ` · ${overPlanCount} over plan` : ""}.`}
        action={data.editable ? (
          <ProjectVerticalsDialog project={data.project} rows={verticalRows} onChanged={reload} />
        ) : null}
        testId="card-pd-vertical-segregation">
        <div className="table-scroll" style={{ maxHeight: 460 }}>
          <table className="w-full border-collapse" data-testid="pd-vertical-segregation-table">
            <thead><tr>
              {["Vertical", "Budget", "Spend", "Remaining", "% Util", "Visibility", "Performance"].map((h, i) => (
                <th key={h} className={`th-sticky px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground ${i === 0 ? "th-sticky-left text-left" : "text-right"}`}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {verticalRows.map((v, i) => {
                const idle = v.spend === 0 && v.budget === 0;
                const over = isOverPlan(v);
                return (
                  <tr key={v.vertical} data-testid={`pd-vertical-row-${v.vertical}`} className={`border-b last:border-0 ${i % 2 ? "row-zebra" : ""} ${idle ? "opacity-55" : ""}`}>
                    <td className="td-sticky-left px-3 py-2 text-sm font-medium">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <Layers className="h-3.5 w-3.5 text-muted-foreground" />{v.vertical}
                        {over && (
                          <Badge variant="outline"
                            className="border-[hsl(var(--danger)/0.45)] bg-[hsl(var(--danger)/0.08)] px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide text-[hsl(var(--danger))]"
                            data-testid={`pd-over-plan-${v.vertical}`}
                            title={`Over plan by ${money(v.spend - v.budget)} · spent ${formatPercent(v.util, 0)} of planned budget`}>
                            Over plan +{money(v.spend - v.budget)}
                          </Badge>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 num text-sm text-muted-foreground"><Money value={v.budget} /></td>
                    <td className="px-3 py-2 num text-sm font-medium"><Money value={v.spend} /></td>
                    <td className="px-3 py-2 num text-sm"><Money value={v.remaining} colorNegative /></td>
                    <td className="px-3 py-2 num text-sm" style={{ color: v.util > 1 ? "hsl(var(--danger))" : "inherit" }}>{v.budget ? formatPercent(v.util, 0) : DASH}</td>
                    <td className="px-3 py-2 num text-sm text-muted-foreground"><Money value={v.visibility} /></td>
                    <td className="px-3 py-2 num text-sm text-muted-foreground"><Money value={v.performance} /></td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t bg-[hsl(var(--surface-2))] font-semibold">
                <td className="td-sticky-left px-3 py-2 text-sm">Total</td>
                <td className="px-3 py-2 num text-sm"><Money value={verticalRows.reduce((a, v) => a + v.budget, 0)} /></td>
                <td className="px-3 py-2 num text-sm"><Money value={verticalRows.reduce((a, v) => a + v.spend, 0)} /></td>
                <td className="px-3 py-2 num text-sm"><Money value={verticalRows.reduce((a, v) => a + v.remaining, 0)} colorNegative /></td>
                <td className="px-3 py-2 num text-sm text-muted-foreground">{DASH}</td>
                <td className="px-3 py-2 num text-sm"><Money value={verticalRows.reduce((a, v) => a + v.visibility, 0)} /></td>
                <td className="px-3 py-2 num text-sm"><Money value={verticalRows.reduce((a, v) => a + v.performance, 0)} /></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </SectionCard>
      )}


      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {pdOn("pd-platform") && (
        <SectionCard title={<EditableText cmsKey="pd.section.platform.title" fallback="Spend by Platform" />}
          description={`${platformAll.length} platform${platformAll.length === 1 ? "" : "s"} · ${money(platformTotal)} total (reconciles to Total Spend).`}
          testId="card-pd-platform">
          <div className="px-2 py-4" style={{ height: 300 }}>
            {platformRows.length === 0 ? <EmptyState title="No spend" message="No platform spend." /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={platformRows} layout="vertical" margin={{ top: 0, right: 12, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
                  <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
                  <YAxis type="category" dataKey="platform" width={120} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
                  <Tooltip content={<ChartTooltip formatter={(v) => moneyFmt(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                  <Bar dataKey="spend" name="Spend" radius={[0, 4, 4, 0]} barSize={13} isAnimationActive={false}>
                    {platformRows.map((c, i) => <Cell key={i} fill={c._other ? "hsl(var(--muted-foreground))" : seriesColor(i)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>
        )}

        {pdOn("pd-monthly-table") && (
        <SectionCard title={<EditableText cmsKey="pd.section.monthly.title" fallback="Monthly Funnel & Revenue" />} testId="card-pd-monthly-table">
          <div className="table-scroll" style={{ maxHeight: 300 }}>
            <table className="w-full border-collapse">
              <thead><tr>
                {["Month", "Leads", "Visits", "Bookings", "Spend", "Revenue"].map((h, i) => (
                  <th key={h} className={`th-sticky px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground ${i === 0 ? "th-sticky-left text-left" : "text-right"}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {data.monthly.map((m, i) => (
                  <tr key={m.month} className={`border-b last:border-0 ${i % 2 ? "row-zebra" : ""}`}>
                    <td className="td-sticky-left px-3 py-2 text-sm font-medium">{m.month}</td>
                    <td className="px-3 py-2 num"><TargetBadge actual={m.leads_actual} target={m.leads_target} /></td>
                    <td className="px-3 py-2 num"><TargetBadge actual={m.sv_actual} target={m.sv_target} /></td>
                    <td className="px-3 py-2 num"><TargetBadge actual={m.booking_actual} target={m.booking_target} showValues /></td>
                    <td className="px-3 py-2 num text-sm"><Money value={m.spend} /></td>
                    <td className="px-3 py-2 num text-sm font-medium"><Money value={m.revenue} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
        )}
      </div>

      {/* Spend line items (detailed outline) */}
      {pdOn("pd-line-items") && (
      <SectionCard title={<EditableText cmsKey="pd.section.line_items.title" fallback="Spend Line Items" />}
        description={`Every spend transaction for ${periodLabel} · ${(data.line_items || []).length} line items · ${money(data.line_items_total || 0)} total.`}
        testId="card-pd-line-items">
        {(data.line_items || []).length === 0 ? (
          <div className="p-4"><EmptyState title="No spend line items" message="No spends recorded for this scope." /></div>
        ) : (
          <div className="table-scroll" style={{ maxHeight: 460 }}>
            <table className="w-full border-collapse" data-testid="pd-line-items-table">
              <thead><tr>
                {["Date", "Month", "Vertical", "Sub Line Item", "Platform", "Vendor", "Type", "Amount"].map((h, i) => (
                  <th key={h} className={`th-sticky px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground ${i === 7 ? "text-right" : "text-left"} ${i === 0 ? "th-sticky-left" : ""}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(data.line_items || []).map((li, i) => (
                  <tr key={i} className={`border-b last:border-0 hover:bg-[hsl(var(--surface-2))] ${i % 2 ? "row-zebra" : ""}`} data-testid="pd-line-item-row">
                    <td className="td-sticky-left px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{li.date || DASH}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{li.month || DASH}</td>
                    <td className="px-3 py-2 text-sm">{li.vertical || DASH}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{li.sub_line_item || DASH}</td>
                    <td className="px-3 py-2 text-xs">{li.platform || DASH}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{li.vendor || DASH}</td>
                    <td className="px-3 py-2 text-xs">
                      {li.vis_perf ? <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">{li.vis_perf}</span> : DASH}
                    </td>
                    <td className="px-3 py-2 num text-sm font-medium"><Money value={li.amount} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t bg-[hsl(var(--surface-2))] font-semibold">
                  <td className="td-sticky-left px-3 py-2 text-sm" colSpan={7}>Total</td>
                  <td className="px-3 py-2 num text-sm"><Money value={data.line_items_total || 0} /></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </SectionCard>
      )}
    </div>
  );
}
